import { Badge } from '@/components/ui/badge'
import { calculateRawDistance, distanceToSimilarity } from '@/lib/ideology'
import { cn } from '@/lib/utils'
import type { IdeologyVector } from '@/types'

interface IdeologyAlignmentBadgeProps {
  candidateIdeology: IdeologyVector
  partyIdeology: IdeologyVector
  /** Show percentage number */
  showPercentage?: boolean
  className?: string
}

/**
 * Displays ideology alignment between a candidate and party as a badge.
 *
 * Alignment is calculated as:
 * 100 - (normalized distance from 0 to 100)
 *
 * Color coding:
 * - 80%+ = green (default badge) - Strong alignment
 * - 60-79% = yellow (secondary badge) - Moderate alignment
 * - <60% = gray (outline badge) - Poor alignment
 */
export function IdeologyAlignmentBadge({
  candidateIdeology,
  partyIdeology,
  showPercentage = true,
  className,
}: IdeologyAlignmentBadgeProps) {
  const distance = calculateRawDistance(candidateIdeology, partyIdeology)
  const alignment = distanceToSimilarity(distance)

  const variant = alignment >= 80 ? 'default' : alignment >= 60 ? 'secondary' : 'outline'

  return (
    <Badge variant={variant} className={cn('text-xs', className)}>
      {showPercentage ? `${alignment}% aligned` : getAlignmentLabel(alignment)}
    </Badge>
  )
}

/**
 * Get a human-readable label for alignment level
 */
function getAlignmentLabel(alignment: number): string {
  if (alignment >= 90) return 'Excellent fit'
  if (alignment >= 80) return 'Strong fit'
  if (alignment >= 70) return 'Good fit'
  if (alignment >= 60) return 'Moderate fit'
  if (alignment >= 50) return 'Weak fit'
  return 'Poor fit'
}
